/**
 * AI Studio — six focused tools rather than one open chat box.
 *
 * Two of them produce something the workspace can act on: "Build a sequence"
 * hands back a journey you can create in one click, and "Find an audience"
 * hands back a segment. The rest return prose.
 */
import { useState } from 'react';
import { useApp } from '../../store/AppContext.jsx';
import Icon from '../../lib/icons.jsx';
import Markdown from '../../lib/markdown.jsx';
import { Card, CardBody, Spinner } from '../../components/Primitives.jsx';
import { TileRow } from '../../components/StatCards.jsx';
import {
  CHANNELS, STUDIO_TOOLS, STUDIO_HINTS, REWRITE_MODES, AUDIENCE_FLAGS, STEP_TYPES
} from '../../constants/marketing.js';
import { audienceOf } from '../../domain/marketing.js';
import * as MAI from '../../services/marketingAI.js';

const PLACEHOLDERS = {
  sequence: 'e.g. stop trials lapsing without a conversation',
  audience: 'e.g. GROWTH clients in Dubai who have never bought an add-on',
  subjects: 'e.g. renewal reminder for annual SUITE accounts',
  rewrite: 'Paste the message…',
  grade: 'Paste the message…',
  reply: 'Paste what they wrote back…'
};

const VALID_STEP_TYPES = STEP_TYPES.map(t => t[0]);

export default function AIStudio() {
  const { S, mkt, mktCtx, saveJourney, saveAudience, toast } = useApp();

  const [tool, setTool] = useState('sequence');
  const [input, setInput] = useState('');
  const [audienceId, setAudienceId] = useState('');
  const [mode, setMode] = useState('shorter');
  const [channel, setChannel] = useState('email');
  const [state, setState] = useState({ loading: false, text: '', parsed: null });

  const audience = mkt.audiences.find(a => a.id === audienceId);
  const needsAudience = ['sequence', 'audience', 'subjects'].includes(tool);

  const run = async () => {
    if (!input.trim() && tool !== 'sequence') {
      toast('Give it something to work with');
      return;
    }
    setState({ loading: true, text: '', parsed: null });

    try {
      if (tool === 'sequence') {
        const parsed = await MAI.buildSequence(S, {
          audiences: mkt.audiences.map(a => ({ id: a.id, name: a.name, size: audienceOf(a, mktCtx).length })),
          aim: audience
            ? { name: audience.name, size: audienceOf(audience, mktCtx).length }
            : null,
          goal: input
        });
        const steps = (parsed.steps || []).filter(s => VALID_STEP_TYPES.includes(s.type));
        setState({
          loading: false,
          parsed: { ...parsed, steps },
          text: `**${parsed.name}**\n\n${parsed.why || ''}\n\n`
            + steps.map((s, i) => `${i + 1}. ${s.type}${s.channel ? ` (${s.channel})` : ''}`
              + `${s.subject ? ` — ${s.subject}` : ''}${s.days ? ` — ${s.days} days` : ''}`).join('\n')
        });
        return;
      }

      if (tool === 'audience') {
        const parsed = await MAI.findAudience(S, {
          description: input,
          flags: AUDIENCE_FLAGS.map(f => f[0]).filter(Boolean),
          plans: S.plans,
          statuses: S.statuses,
          types: S.types
        });
        const preview = audienceOf(parsed, mktCtx).length;
        setState({
          loading: false,
          parsed,
          text: `**${parsed.name}**\n\n${parsed.why || ''}\n\n`
            + `Source: ${parsed.src || 'clients'} · filter: ${parsed.flag || 'everyone'}\n\n`
            + `**${preview}** qualify right now.`
        });
        return;
      }

      const text = await MAI.runStudioTool(S, tool, {
        input,
        audience: audience ? { name: audience.name, size: audienceOf(audience, mktCtx).length } : null,
        mode,
        channel
      });
      setState({ loading: false, text, parsed: null });
    } catch (e) {
      setState({ loading: false, text: e.message, parsed: null });
      toast(e.message);
    }
  };

  /** Turn the parsed result into a real journey or audience. */
  const create = () => {
    if (tool === 'sequence' && state.parsed) {
      const known = mkt.audiences.some(a => a.id === state.parsed.audienceId);
      saveJourney(null, {
        name: state.parsed.name || 'New journey',
        audience: known ? state.parsed.audienceId : (audienceId || mkt.audiences[0]?.id || ''),
        enabled: false,
        recool: 90,
        exitOnLeave: true,
        steps: state.parsed.steps || []
      });
      setState({ loading: false, text: '', parsed: null });
      return;
    }
    if (tool === 'audience' && state.parsed) {
      const { name, src, flag, status, plan, type, owner } = state.parsed;
      saveAudience(null, {
        name: name || 'New audience',
        src: src === 'contacts' ? 'contacts' : 'clients',
        flag: flag || '', status: status || '', plan: plan || '', type: type || '', owner: owner || ''
      });
      setState({ loading: false, text: '', parsed: null });
    }
  };

  const copy = () => {
    if (!navigator.clipboard) { toast('Clipboard unavailable'); return; }
    navigator.clipboard.writeText(state.text).then(() => toast('Copied'), () => toast('Copy failed'));
  };

  return (
    <>
      <TileRow
        tiles={[
          ['Tools', STUDIO_TOOLS.length, 'wand', 'g-pink'],
          ['Journeys', (mkt.journeys || []).length, 'flow', 'g-purple'],
          ['Templates', mkt.templates.length, 'file', 'g-orange'],
          ['Audiences', mkt.audiences.length, 'target', 'g-cyan'],
          ['Campaigns', mkt.campaigns.length, 'mega', 'g-blue'],
          ['Queued', (mkt.queue || []).filter(q => q.status === 'pending').length, 'send', 'g-amber']
        ]}
      />

      <Card>
        <div className="tabs">
          {STUDIO_TOOLS.map(([k, label, icon]) => (
            <button
              type="button"
              key={k}
              className={tool === k ? 'on' : ''}
              onClick={() => { setTool(k); setState({ loading: false, text: '', parsed: null }); }}
            >
              <Icon name={icon} />{label}
            </button>
          ))}
        </div>

        <div className="setmain">
          <div className="set">
            <h4>{(STUDIO_TOOLS.find(x => x[0] === tool) || ['', ''])[1]}</h4>
            <p>{STUDIO_HINTS[tool]}</p>

            {tool === 'rewrite' ? (
              <div className="g2" style={{ marginBottom: 11 }}>
                <div className="num">
                  <label>Change it to be</label>
                  <select value={mode} onChange={e => setMode(e.target.value)}>
                    {REWRITE_MODES.map(([v, n]) => <option key={v} value={v}>{n}</option>)}
                  </select>
                </div>
                <div className="num">
                  <label>For which channel</label>
                  <select value={channel} onChange={e => setChannel(e.target.value)}>
                    {CHANNELS.map(([v, n]) => <option key={v} value={v}>{n}</option>)}
                  </select>
                </div>
              </div>
            ) : null}

            {needsAudience ? (
              <div className="num" style={{ marginBottom: 11 }}>
                <label>Audience to aim at</label>
                <select value={audienceId} onChange={e => setAudienceId(e.target.value)}>
                  <option value="">— let it choose —</option>
                  {mkt.audiences.map(a => (
                    <option key={a.id} value={a.id}>
                      {a.name} ({audienceOf(a, mktCtx).length})
                    </option>
                  ))}
                </select>
              </div>
            ) : null}

            <textarea
              style={{ minHeight: 120 }}
              placeholder={PLACEHOLDERS[tool]}
              value={input}
              onChange={e => setInput(e.target.value)}
            />

            <div className="rowb" style={{ marginTop: 11 }}>
              <button type="button" className="btn primary" onClick={run} disabled={state.loading}>
                {state.loading ? <><Spinner />Working…</> : <><Icon name="wand" />Run it</>}
              </button>
              {state.parsed && tool === 'sequence'
                ? <button type="button" className="btn" onClick={create}>Create this journey</button> : null}
              {state.parsed && tool === 'audience'
                ? <button type="button" className="btn" onClick={create}>Create this audience</button> : null}
              {state.text ? <button type="button" className="btn" onClick={copy}>Copy the output</button> : null}
            </div>
          </div>

          {state.text || state.loading ? (
            <div className="set">
              <h4>Result</h4>
              <CardBody className="brief" style={{ padding: 0 }}>
                {state.loading ? <span className="muted">Thinking…</span> : <Markdown text={state.text} />}
              </CardBody>
            </div>
          ) : null}
        </div>
      </Card>
    </>
  );
}
